'use client'

import React from 'react'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import Link from 'next/link'
import Image from 'next/image'
import { X } from 'lucide-react'

interface FarmersMarketsModalProps {
  isOpen: boolean
  onClose: () => void
  // Optional wording, so other pages can reuse the modal. Defaults are the home page's text.
  title?: string
  subtitle?: string
}

const FarmersMarketsModal: React.FC<FarmersMarketsModalProps> = ({
  isOpen,
  onClose,
  title = 'Connect with Local Farmers',
  subtitle = 'Join Culineer to discover farmers and markets near you',
}) => {
  const features = [
    {
      icon: '🌾',
      title: 'Connect with Local Farmers',
      description: 'Find and connect with farmers in your area',
    },
    {
      icon: '🥬',
      title: 'Discover Fresh Ingredients',
      description: 'Access seasonal produce and local specialties',
    },
    {
      icon: '👥',
      title: 'Join a Community',
      description: 'Connect with other home cooks and food enthusiasts',
    },
    {
      icon: '📱',
      title: 'Stay Updated',
      description: 'Get notifications about new products and markets',
    },
  ]

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl w-full max-h-[90vh] overflow-y-auto bg-white p-0">
        <div className="relative">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 rounded-full bg-white p-2 shadow-md hover:bg-gray-100 transition-colors"
          >
            <X className="h-5 w-5 text-gray-600" />
          </button>

          {/* Hero Image */}
          <div className="relative h-48 w-full overflow-hidden rounded-t-lg">
            <Image
              src="/images/farmers-near-you.png"
              alt="Local Farmers Markets"
              fill
              className="object-cover"
              onError={(e) => {
                e.currentTarget.style.display = 'none'
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <h2 className="text-3xl font-bold text-white mb-2">{title}</h2>
              <p className="text-white/90">{subtitle}</p>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Features Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="flex items-start space-x-3 p-4 rounded-lg bg-[#F3FAEB] hover:bg-[#E5F3D4] transition-colors"
                >
                  <span className="text-3xl">{feature.icon}</span>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {feature.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Benefits Section */}
            <div className="mb-6 rounded-lg bg-gradient-to-br from-[#F3FAEB] to-[#E5F3D4] p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Why Join Culineer?
              </h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-center space-x-2">
                  <span className="text-[#85C349]">✓</span>
                  <span>Access to thousands of local farmers and markets</span>
                </li>
                <li className="flex items-center space-x-2">
                  <span className="text-[#85C349]">✓</span>
                  <span>Find recipes using local, seasonal ingredients</span>
                </li>
                <li className="flex items-center space-x-2">
                  <span className="text-[#85C349]">✓</span>
                  <span>Support your local food community</span>
                </li>
                <li className="flex items-center space-x-2">
                  <span className="text-[#85C349]">✓</span>
                  <span>Get personalized recommendations</span>
                </li>
              </ul>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                href="/auth/join-culineer"
                onClick={onClose}
                className="flex-1 rounded-full bg-[#8BC34A] px-8 py-3 font-semibold text-white text-center transition-all hover:bg-[#7cb342] focus:outline-none focus:ring-2 focus:ring-[#8BC34A] focus:ring-offset-2"
              >
                Sign up for free
              </Link>
              <Link
                href="/auth/login"
                onClick={onClose}
                className="flex-1 rounded-full border-2 border-[#8BC34A] bg-white px-8 py-3 font-semibold text-[#8BC34A] text-center transition-all hover:bg-[#F3FAEB] focus:outline-none focus:ring-2 focus:ring-[#8BC34A] focus:ring-offset-2"
              >
                Log in
              </Link>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default FarmersMarketsModal

